// src/pages/Setor7Page.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Ícones
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SpeedIcon from '@mui/icons-material/Speed';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import Lottie from 'lottie-react';
import cartAnimation from '../assets/estrategic.json';
import { useThemeMode } from '../context/ThemeContext';
import Chart from 'react-apexcharts';

const API_BASE = process.env.REACT_APP_API_BASE ?? '';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function Setor7Page() {
  const { mode, setMode } = useThemeMode();
  const isDark = mode === 'dark';

  const navigate = useNavigate();
  const handleVoltarClick = () => navigate('/menu');

  // Estado
  const [loadingDados, setLoadingDados] = useState(true);
  const [error, setError] = useState(null);
  const [operacionalData, setOperacionalData] = useState([]);
  const [operacionalData12M, setOperacionalData12M] = useState([]); // Dados dos últimos 12 meses para metas
  
  // Estado para COMPRAS
  const [loadingCompras, setLoadingCompras] = useState(false);
  const [comprasData, setComprasData] = useState([]);
  const [startDateCompras, setStartDateCompras] = useState(dayjs(`${dayjs().year()}-${String(dayjs().month() + 1).padStart(2, '0')}-01`));
  const [endDateCompras, setEndDateCompras] = useState(dayjs().endOf('month'));
  
  // Estados para datas de query de compras (apenas mudam ao clicar "Filtrar")
  const [queryStartDateCompras, setQueryStartDateCompras] = useState(dayjs(`${dayjs().year()}-${String(dayjs().month() + 1).padStart(2, '0')}-01`));
  const [queryEndDateCompras, setQueryEndDateCompras] = useState(dayjs().endOf('month'));

  // Handler para aplicar filtro de compras
  const handleApplyFilterCompras = () => {
    setQueryStartDateCompras(startDateCompras);
    setQueryEndDateCompras(endDateCompras);
  };

  // Estado para GRUPOS DE COMPRAS
  const [comprasGrupoData, setComprasGrupoData] = useState([]);

  const currentDate = dayjs();
  const [startDate, setStartDate] = useState(currentDate.startOf('month'));
  const [endDate, setEndDate] = useState(currentDate.endOf('month'));
  
  // Estados para datas de query (apenas mudam ao clicar "Filtrar")
  const [queryStartDate, setQueryStartDate] = useState(currentDate.startOf('month'));
  const [queryEndDate, setQueryEndDate] = useState(currentDate.endOf('month'));

  // Handler para aplicar filtro
  const handleApplyFilter = () => {
    setQueryStartDate(startDate);
    setQueryEndDate(endDate);
  };

  // Fetch dados do backend
  useEffect(() => {
    if (!queryStartDate || !queryEndDate) return;

    let cancelled = false;
    setLoadingDados(true);
    setError(null);

    const payload = {
      startDate: queryStartDate.format('YYYY-MM-DD'),
      endDate: queryEndDate.format('YYYY-MM-DD'),
    };

    console.log('🔍 Setor7Page - Iniciando fetch com:', payload);

    axios
      .get(`${API_BASE}/api/estrategico/operacional`, {
        params: payload,
        headers: { Authorization: 'chave-super-secreta-123' },
      })
      .then((res) => {
        if (cancelled) return;

        console.log('✅ Setor7Page - Dados recebidos:', res.data);
        console.log(`🔍 Dados recebidos para período ${payload.startDate} a ${payload.endDate}:`, res.data.length, 'registros');
        
        // Log dos períodos únicos recebidos
        if (Array.isArray(res.data) && res.data.length > 0) {
          const periodosUnicos = [...new Set(res.data.map(r => r.periodo))].sort();
          console.log('🗓️ Períodos únicos nos dados:', periodosUnicos);
          
          // Log específico da São Pedro para debug
          const saoPedroRecebidos = res.data.filter(r => r.empresa && r.empresa.trim().toUpperCase() === 'SÃO PEDRO');
          console.log(`📊 São Pedro: ${saoPedroRecebidos.length} registros recebidos do backend`);
        }

        if (!Array.isArray(res.data)) {
          throw new Error('Resposta inválida em /estrategico/operacional (esperado array).');
        }

        setOperacionalData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error('❌ Erro na API:', err?.response || err?.message);

        setError(
          err?.response
            ? `Erro ${err.response.status}: ${err.response.data?.error || err.response.statusText}`
            : 'Não foi possível carregar os dados'
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingDados(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryStartDate, queryEndDate]);

  // Fetch dados dos últimos 12 meses para calcular metas (SEMPRE independente do período selecionado)
  useEffect(() => {
    let cancelled = false;

    // IMPORTANTE: Usa data ATUAL como referência, NÃO endDate
    // Assim os últimos 12 meses são sempre os mesmos, independente do filtro selecionado
    const dataAtual = dayjs();
    const start12M = dataAtual.subtract(12, 'months');

    const payload = {
      startDate: start12M.format('YYYY-MM-DD'),
      endDate: dataAtual.format('YYYY-MM-DD'),
    };

    console.log('🔍 Buscando últimos 12 meses para metas (FIXA):', payload);

    axios
      .get(`${API_BASE}/api/estrategico/operacional`, {
        params: payload,
        headers: { Authorization: 'chave-super-secreta-123' },
      })
      .then((res) => {
        if (cancelled) return;

        console.log('✅ Dados de 12 meses para metas recebidos:', res.data.length, 'registros');

        if (!Array.isArray(res.data)) {
          throw new Error('Resposta inválida para 12 meses');
        }

        setOperacionalData12M(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('❌ Erro ao buscar 12 meses para metas:', err?.message);
        setOperacionalData12M([]); // Fallback para vazio
      });

    return () => {
      cancelled = true;
    };
  }, []); // Dependência vazia = executar UMA VEZ ao montar

  // Fetch dados de COMPRAS do backend (separado do operacional)
  useEffect(() => {
    if (!queryStartDateCompras || !queryEndDateCompras) return;

    let cancelled = false;
    setLoadingCompras(true);

    const payload = {
      startDate: queryStartDateCompras.format('YYYY-MM-DD'),
      endDate: queryEndDateCompras.format('YYYY-MM-DD'),
    };

    console.log('🔍 Compras - Iniciando fetch com:', payload);

    axios
      .get(`${API_BASE}/api/estrategico/compras`, {
        params: payload,
        headers: { Authorization: 'chave-super-secreta-123' },
      })
      .then((res) => {
        if (cancelled) return;

        console.log('✅ Dados de Compras recebidos:', res.data);

        if (!Array.isArray(res.data)) {
          throw new Error('Resposta inválida em /compras (esperado array).');
        }

        setComprasData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error('❌ Erro na API de Compras:', err?.response || err?.message);
        setComprasData([]); // Fallback para vazio
      })
      .finally(() => {
        if (!cancelled) setLoadingCompras(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryStartDateCompras, queryEndDateCompras]);

  // Fetch dados de GRUPOS DE COMPRAS do backend
  useEffect(() => {
    if (!queryStartDateCompras || !queryEndDateCompras) return;

    let cancelled = false;

    const payload = {
      startDate: queryStartDateCompras.format('YYYY-MM-DD'),
      endDate: queryEndDateCompras.format('YYYY-MM-DD'),
    };

    console.log('🔍 Grupos de Compras - Iniciando fetch com:', payload);

    axios
      .get(`${API_BASE}/api/estrategico/comp-grupo`, {
        params: payload,
        headers: { Authorization: 'chave-super-secreta-123' },
      })
      .then((res) => {
        if (cancelled) return;

        console.log('✅ Dados de Grupos de Compras recebidos:', res.data);

        if (!Array.isArray(res.data)) {
          throw new Error('Resposta inválida em /comp-grupo (esperado array).');
        }

        setComprasGrupoData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error('❌ Erro na API de Grupos de Compras:', err?.response || err?.message);
        setComprasGrupoData([]); // Fallback para vazio
      });

    return () => {
      cancelled = true;
    };
  }, [queryStartDateCompras, queryEndDateCompras]);

  // Calcula métricas por empresa
  const eficienciasPorEmpresa = useMemo(() => {
    if (!operacionalData || operacionalData.length === 0) {
      console.log('⚠️ Sem dados operacionais');
      return {};
    }

    console.log('🔢 Calculando eficiências para', operacionalData.length, 'registros');
    console.log('📋 Amostra de dados:', operacionalData.slice(0, 3));

    // Extrai empresas únicas do dataset
    const empresasUnicas = [...new Set(operacionalData.map(r => r.empresa).filter(Boolean))];
    console.log('🏢 Empresas encontradas no dataset:', empresasUnicas);

    const resultado = {};

    // Calcula para TODAS as empresas do dataset, não apenas a lista predefinida
    empresasUnicas.forEach((empresa) => {
      const registrosEmpresa = operacionalData.filter(
        (r) => r.empresa && String(r.empresa).trim() === String(empresa).trim()
      );

      console.log(`📊 Empresa "${empresa}": ${registrosEmpresa.length} registros`);

      if (registrosEmpresa.length === 0) {
        resultado[empresa] = {
          kmProgramada: 0,
          kmCatPerdida: 0,
          kmPerdida: 0,
          kmRealizada: 0,
          eficiencia: 0,
        };
        return;
      }

      // Cálculos seguindo a metodologia DAX do Power BI
      const kmProgramada = registrosEmpresa.reduce((sum, r) => sum + Number(r.km_programada || 0), 0);

      const kmNaoRealizada = registrosEmpresa.reduce(
        (sum, r) => sum + Number(r.km_nao_realizada || 0),
        0
      );

      const kmCatPerdida = registrosEmpresa.reduce(
        (sum, r) => sum + Number(r.km_cat_dif_prog_nao_realizada || 0),
        0
      );

      const kmPerdida = kmNaoRealizada + kmCatPerdida;
      const kmRealizada = kmProgramada - kmPerdida;

      // Fórmula correta: Eficiência = 1 - (km_perdidos / km_programada)
      const eficiencia = kmProgramada > 0 ? (1 - (kmPerdida / kmProgramada)) * 100 : 0;

      resultado[empresa] = {
        kmProgramada,
        kmCatPerdida,
        kmPerdida,
        kmRealizada,
        eficiencia: Math.max(0, Math.min(100, eficiencia)), // garante entre 0 e 100
      };

      console.log(`✔️ "${empresa}": Eficiência KM ${resultado[empresa].eficiencia.toFixed(4)}%`);
      console.log(`   Detalhes: km_prog=${kmProgramada}, km_perdida=${kmPerdida}`);
      
      // Log específico para São Pedro
      if (empresa.trim().toUpperCase() === 'SÃO PEDRO') {
        console.log('🎯 SÃO PEDRO QUILOMETRAGEM DEBUG:');
        console.log(`   Registros: ${registrosEmpresa.length}`);
        console.log(`   KM Programada: ${kmProgramada}`);
        console.log(`   KM Não Realizada: ${kmNaoRealizada}`);
        console.log(`   KM Cat Perdida: ${kmCatPerdida}`);
        console.log(`   KM Perdida Total: ${kmPerdida}`);
        console.log(`   Fórmula: 1 - (${kmPerdida} / ${kmProgramada}) * 100`);
        console.log(`   Resultado: ${eficiencia.toFixed(4)}%`);
      }
    });

    console.log('📈 Resultado final:', resultado);
    return resultado;
  }, [operacionalData]);

  // Calcula eficiência de viagens por empresa
  const eficienciaViagensPorEmpresa = useMemo(() => {
    if (!operacionalData || operacionalData.length === 0) {
      console.log('⚠️ Sem dados operacionais para viagens');
      return {};
    }

    console.log('🔢 Calculando eficiências de viagens para', operacionalData.length, 'registros');

    // Extrai empresas únicas do dataset
    const empresasUnicas = [...new Set(operacionalData.map(r => r.empresa).filter(Boolean))];
    console.log('🏢 Empresas encontradas no dataset (viagens):', empresasUnicas);

    const resultado = {};

    // Calcula para TODAS as empresas do dataset
    empresasUnicas.forEach((empresa) => {
      const registrosEmpresa = operacionalData.filter(
        (r) => r.empresa && String(r.empresa).trim() === String(empresa).trim()
      );

      console.log(`📊 Empresa "${empresa}": ${registrosEmpresa.length} registros para viagens`);

      if (registrosEmpresa.length === 0) {
        resultado[empresa] = {
          viagensProgramadas: 0,
          viagensNaoRealizadas: 0,
          viagensRealizadas: 0,
          eficiencia: 0,
        };
        return;
      }

      // Cálculos seguindo a metodologia DAX do Power BI
      const viagensProgramadas = registrosEmpresa.reduce((sum, r) => sum + Number(r.v_programadas || 0), 0);

      const viagensNaoRealizadas = registrosEmpresa.reduce(
        (sum, r) => sum + Number(r.v_nao_realizadas || 0),
        0
      );

      const viagensRealizadas = viagensProgramadas - viagensNaoRealizadas;

      // Fórmula correta: Eficiência = 1 - (viagens_não_realizadas / viagens_programadas)
      const eficiencia = viagensProgramadas > 0 ? (1 - (viagensNaoRealizadas / viagensProgramadas)) * 100 : 0;

      resultado[empresa] = {
        viagensProgramadas,
        viagensNaoRealizadas,
        viagensRealizadas,
        eficiencia: Math.max(0, Math.min(100, eficiencia)), // garante entre 0 e 100
      };

      console.log(`✔️ "${empresa}": Eficiência de Viagem ${resultado[empresa].eficiencia.toFixed(4)}%`);
      console.log(`   Detalhes: v_prog=${viagensProgramadas}, v_nao_realiz=${viagensNaoRealizadas}`);
      
      // Log específico para São Pedro
      if (empresa.trim().toUpperCase() === 'SÃO PEDRO') {
        console.log('🎯 SÃO PEDRO VIAGEM DEBUG:');
        console.log(`   Registros: ${registrosEmpresa.length}`);
        console.log(`   V. Programadas: ${viagensProgramadas}`);
        console.log(`   V. Não Realizadas: ${viagensNaoRealizadas}`);
        console.log(`   Fórmula: 1 - (${viagensNaoRealizadas} / ${viagensProgramadas}) * 100`);
        console.log(`   Resultado: ${eficiencia.toFixed(4)}%`);
      }
    });

    console.log('📈 Resultado final (viagens):', resultado);
    return resultado;
  }, [operacionalData]);

  // Dados detalhados de São Pedro
  const dadosSaoPedro = useMemo(() => {
    if (!operacionalData || operacionalData.length === 0) {
      console.log('⚠️ Sem dados operacionais para São Pedro');
      return null;
    }

    console.log('🔍 Dataset completo recebido:', operacionalData.length, 'registros');
    console.log('🗓️ PERÍODO FILTRADO:', queryStartDate.format('YYYY-MM-DD'), 'a', queryEndDate.format('YYYY-MM-DD'));
    console.log('🔍 Empresas únicas no dataset:', [...new Set(operacionalData.map(r => r.empresa))]);

    const registrosSaoPedro = operacionalData.filter(
      (r) => r.empresa && String(r.empresa).trim().toUpperCase() === 'SÃO PEDRO'
    );

    console.log(`📊 Registros de São Pedro encontrados: ${registrosSaoPedro.length}`);

    if (registrosSaoPedro.length === 0) {
      console.log('⚠️ Nenhum registro encontrado para São Pedro');
      return null;
    }

    // Km Programada
    const kmProgramada = registrosSaoPedro.reduce((sum, r) => sum + Number(r.km_programada || 0), 0);

    // Km Realizada
    const kmNaoRealizada = registrosSaoPedro.reduce((sum, r) => sum + Number(r.km_nao_realizada || 0), 0);
    const kmCatPerdida = registrosSaoPedro.reduce(
      (sum, r) => sum + Number(r.km_cat_dif_prog_nao_realizada || 0),
      0
    );
    const kmPerdida = kmNaoRealizada + kmCatPerdida;
    const kmRealizada = kmProgramada - kmPerdida;
    const percentualKmPerdida = kmProgramada > 0 ? (kmPerdida / kmProgramada) * 100 : 0;
    // Fórmula correta: Eficiência = 1 - (km_perdidos / km_programada)
    const eficienciaKm = kmProgramada > 0 ? (1 - (kmPerdida / kmProgramada)) * 100 : 0;

    // V. Programadas
    const viagensProgramadas = registrosSaoPedro.reduce((sum, r) => sum + Number(r.v_programadas || 0), 0);

    // V. Realizadas
    const viagensNaoRealizadas = registrosSaoPedro.reduce((sum, r) => sum + Number(r.v_nao_realizadas || 0), 0);
    const viagensRealizadas = viagensProgramadas - viagensNaoRealizadas;
    const percentualViagensPerdidas = viagensProgramadas > 0 ? (viagensNaoRealizadas / viagensProgramadas) * 100 : 0;
    // Fórmula correta: Eficiência = 1 - (viagens_não_realizadas / viagens_programadas)
    const eficienciaViagem = viagensProgramadas > 0 ? (1 - (viagensNaoRealizadas / viagensProgramadas)) * 100 : 0;

    const resultado = {
      kmProgramada,
      kmRealizada,
      kmPerdida,
      percentualKmPerdida,
      eficienciaKm,
      viagensProgramadas,
      viagensRealizadas,
      viagensNaoRealizadas,
      percentualViagensPerdidas,
      eficienciaViagem,
    };

    // Log específico para São Pedro
    console.log('🎯 DADOS SÃO PEDRO DEBUG:');
    console.log(`   Registros: ${registrosSaoPedro.length}`);
    console.log(`   KM Programada: ${kmProgramada.toLocaleString()}`);
    console.log(`   KM Perdida: ${kmPerdida.toLocaleString()} (nao_realizada: ${kmNaoRealizada} + cat_perdida: ${kmCatPerdida})`);
    console.log(`   KM Eficiência: ${eficienciaKm.toFixed(4)}%`);
    console.log(`   V. Programadas: ${viagensProgramadas.toLocaleString()}`);
    console.log(`   V. Não Realizadas: ${viagensNaoRealizadas}`);
    console.log(`   Viagem Eficiência: ${eficienciaViagem.toFixed(4)}%`);

    console.log('✔️ Dados de São Pedro calculados:', resultado);
    return resultado;
  }, [operacionalData, queryStartDate, queryEndDate]);

  // Frota Operacional mais recente de São Pedro
  const frotaSaoPedroRecente = useMemo(() => {
    if (!operacionalData || operacionalData.length === 0) {
      console.log('⚠️ Sem dados operacionais para frota');
      return null;
    }

    // Filtra apenas dados de São Pedro
    const registrosSaoPedro = operacionalData.filter(
      (r) => r.empresa && String(r.empresa).trim().toUpperCase() === 'SÃO PEDRO'
    );

    if (registrosSaoPedro.length === 0) {
      console.log('⚠️ Nenhum registro encontrado para São Pedro - Frota');
      return null;
    }

    // Ordena por data decrescente para pegar o mais recente
    const registrosOrdenados = registrosSaoPedro
      .filter((r) => r.frota !== null && r.frota !== undefined)
      .sort((a, b) => {
        const dataA = dayjs(a.data || a.DATA);
        const dataB = dayjs(b.data || b.DATA);
        return dataB.diff(dataA); // Decrescente (mais recente primeiro)
      });

    if (registrosOrdenados.length === 0) {
      console.log('⚠️ Nenhum valor de frota encontrado para São Pedro');
      return null;
    }

    const frotaMaisRecente = registrosOrdenados[0].frota;
    const dataMaisRecente = registrosOrdenados[0].data || registrosOrdenados[0].DATA;

    console.log('🚌 Frota mais recente de São Pedro:', frotaMaisRecente, 'em', dataMaisRecente);

    return {
      frota: Number(frotaMaisRecente),
      data: dayjs(dataMaisRecente).format('DD/MM/YYYY'),
    };
  }, [operacionalData]);

  // Meta Histórica SP - Quilometragem
  // HARDCODED EM 99,50% (Solicitação do Diretor)
  // Para voltar ao cálculo automático de Média Ponderada dos Últimos 12 Meses,
  // veja o arquivo: LOGICA_CALCULO_METAS.txt
  const metaKmQuiletragem = 99.50;

  // Meta Histórica SP - Viagem
  // HARDCODED EM 99,50% (Solicitação do Diretor)
  // Para voltar ao cálculo automático de Média Ponderada dos Últimos 12 Meses,
  // veja o arquivo: LOGICA_CALCULO_METAS.txt
  const metaViagemSP = 99.50;

  // Maior Ef. Quilometragem - Últimos 12 meses (INDEPENDENTE do período selecionado)
  const maiorEfKm = useMemo(() => {
    if (!operacionalData12M || operacionalData12M.length === 0) {
      console.log('⚠️ Sem dados operacionais de 12 meses para maior EF KM');
      return { eficiencia: 0, mes: '', mesFormatado: '' };
    }

    // Agrupa dados por mês (YYYYMM)
    const mesesData = {};

    operacionalData12M.forEach((r) => {
      if (r.empresa && String(r.empresa).trim().toUpperCase() === 'SÃO PEDRO') {
        const data = dayjs(r.data || r.DATA);
        const mesKey = data.format('YYYYMM');

        if (!mesesData[mesKey]) {
          mesesData[mesKey] = [];
        }

        mesesData[mesKey].push(r);
      }
    });

    console.log('🔍 MAIOR EF KM DEBUG - Meses encontrados (últimos 12M):', Object.keys(mesesData).sort());

    // Calcula eficiência para cada mês e encontra a maior
    let maiorEficiencia = 0;
    let mesMaiorEficiencia = '';

    Object.keys(mesesData).forEach((mesKey) => {
      const registros = mesesData[mesKey];
      const kmProgramada = registros.reduce((sum, r) => sum + Number(r.km_programada || 0), 0);

      if (kmProgramada === 0) {
        console.log(`   [${mesKey}] ⚠️ IGNORADO - km_programada = 0`);
        return;
      }

      const kmNaoRealizada = registros.reduce((sum, r) => sum + Number(r.km_nao_realizada || 0), 0);
      const kmCatPerdida = registros.reduce(
        (sum, r) => sum + Number(r.km_cat_dif_prog_nao_realizada || 0),
        0
      );

      const kmRealizada = kmProgramada - (kmNaoRealizada + kmCatPerdida);
      const eficiencia = (kmRealizada / kmProgramada) * 100;
      const eficienciaLimitada = Math.max(0, Math.min(100, eficiencia));

      console.log(`   [${mesKey}] km_prog=${kmProgramada}, km_perdida=${kmNaoRealizada + kmCatPerdida}, ef=${eficienciaLimitada.toFixed(2)}%`);

      if (eficienciaLimitada > maiorEficiencia) {
        maiorEficiencia = eficienciaLimitada;
        mesMaiorEficiencia = mesKey;
        console.log(`   [${mesKey}] ✓ NOVA MÁXIMA KM: ${maiorEficiencia.toFixed(2)}%`);
      }
    });

    // Formata o mês (YYYYMM) para formato legível (ex: "Ago/2025")
    let mesFormatado = '';
    if (mesMaiorEficiencia) {
      const dataMes = dayjs(mesMaiorEficiencia, 'YYYYMM');
      mesFormatado = dataMes.format('MMM/YYYY').replace(/\./g, ''); // Remove ponto no nome do mês
    }

    const resultado = {
      eficiencia: maiorEficiencia,
      mes: mesMaiorEficiencia,
      mesFormatado,
    };

    console.log(`✔️ Maior EF KM SP (últimos 12M COMPLETOS): ${maiorEficiencia.toFixed(2)}% em ${mesMaiorEficiencia} (${mesFormatado})`);
    return resultado;
  }, [operacionalData12M]);

  // Maior Ef. Viagem - Últimos 12 meses (SEMPRE INDEPENDENTE DO FILTRO)
  const maiorEfViagem = useMemo(() => {
    if (!operacionalData12M || operacionalData12M.length === 0) {
      console.log('⚠️ Sem dados operacionais de 12 meses para maior EF Viagem');
      return { eficiencia: 0, mes: '', mesFormatado: '' };
    }

    // IMPORTANTE: Filtra apenas registros de São Pedro que têm dados de viagem válidos
    const registrosValidos = operacionalData12M.filter((r) => {
      return r.empresa && String(r.empresa).trim().toUpperCase() === 'SÃO PEDRO' &&
             Number(r.v_programadas || 0) > 0;
    });

    console.log(`ℹ️ Registros de São Pedro com v_programadas > 0: ${registrosValidos.length} / ${operacionalData12M.length}`);

    if (registrosValidos.length === 0) {
      console.log('⚠️ NENHUM registro com dados de viagem válidos!');
      // Log para debug: qual é o primeiro registro?
      if (operacionalData12M.length > 0) {
        console.log('🔍 Primeiro registro do dataset:', {
          empresa: operacionalData12M[0].empresa,
          v_programadas: operacionalData12M[0].v_programadas,
          km_programada: operacionalData12M[0].km_programada,
        });
      }
      return { eficiencia: 0, mes: '', mesFormatado: '' };
    }

    // Agrupa apenas registros válidos por mês (YYYYMM)
    const mesesData = {};

    registrosValidos.forEach((r) => {
      const data = dayjs(r.data || r.DATA);
      const mesKey = data.format('YYYYMM');

      if (!mesesData[mesKey]) {
        mesesData[mesKey] = [];
      }

      mesesData[mesKey].push(r);
    });

    console.log('🔍 MAIOR EF VIAGEM - Meses com dados válidos:', Object.keys(mesesData).sort());

    // Calcula eficiência para cada mês e encontra a maior
    let maiorEficiencia = 0;
    let mesMaiorEficiencia = '';

    Object.keys(mesesData).forEach((mesKey) => {
      const registros = mesesData[mesKey];
      const viagensProgramadas = registros.reduce((sum, r) => sum + Number(r.v_programadas || 0), 0);
      const viagensNaoRealizadas = registros.reduce((sum, r) => sum + Number(r.v_nao_realizadas || 0), 0);

      // Fórmula correta: Eficiência = 1 - (viagens_não_realizadas / viagens_programadas)
      const eficiencia = (1 - (viagensNaoRealizadas / viagensProgramadas)) * 100;
      const eficienciaLimitada = Math.max(0, Math.min(100, eficiencia));

      console.log(`   [${mesKey}] v_prog=${viagensProgramadas}, v_nao_realiz=${viagensNaoRealizadas}, ef=${eficienciaLimitada.toFixed(2)}%`);

      if (eficienciaLimitada > maiorEficiencia) {
        maiorEficiencia = eficienciaLimitada;
        mesMaiorEficiencia = mesKey;
      }
    });

    // Formata o mês (YYYYMM) para formato legível (ex: "Ago/2025")
    let mesFormatado = '';
    if (mesMaiorEficiencia) {
      const dataMes = dayjs(mesMaiorEficiencia, 'YYYYMM');
      mesFormatado = dataMes.format('MMM/YYYY').replace(/\./g, ''); // Remove ponto no nome do mês
    }

    const resultado = {
      eficiencia: maiorEficiencia,
      mes: mesMaiorEficiencia,
      mesFormatado,
    };

    console.log(`✅ Maior EF Viagem SP: ${maiorEficiencia.toFixed(2)}% em ${mesMaiorEficiencia} (${mesFormatado})`);
    return resultado;
  }, [operacionalData12M]);

  // Métricas de COMPRAS
  const metricsCompras = useMemo(() => {
    if (!comprasData || comprasData.length === 0) {
      console.log('⚠️ Sem dados de compras');
      return {
        custoRemunerado: 0,
        gastoReal: 0,
        diferenca1: 0,
        variacao1Percentual: 0,
        variacao1Direcao: 'neutral',
        custoImmu: 0,
        custoEmpresa: 0,
        diferenca2: 0,
        variacao2Percentual: 0,
        variacao2Direcao: 'neutral',
      };
    }

    // Cálculos da tabela compras
    const totalKmAcop = comprasData.reduce((sum, r) => sum + Number(r.km_acop_realizada || 0), 0);
    const totalCustoRemunerado = comprasData.reduce((sum, r) => sum + Number(r.custo_remunerado || 0), 0);
    const totalGastoReal = comprasData.reduce((sum, r) => sum + Number(r.gasto_real || 0), 0);

    // Card 1: CUSTO REMUNERADO
    const custoRemunerado = totalCustoRemunerado;

    // Card 2: REALIZADO (GASTO REAL)
    const gastoReal = totalGastoReal;

    // Card 3: DIFERENÇA (CUSTO REMUNERADO - GASTO REAL)
    const diferenca1 = custoRemunerado - gastoReal;
    // Variação percentual: se a diferença aumenta (fica mais positiva), é para mais (seta vermelha para cima)
    // Se diminui (fica mais negativa ou menos positiva), é para menos (seta verde para baixo)
    const variacao1Percentual = gastoReal > 0 ? Math.abs((diferenca1 / gastoReal) * 100) : 0;
    const variacao1Direcao = diferenca1 > 0 ? 'up' : 'down'; // up = vermelho, down = verde

    // Card 4: CUSTO IMMU (CUSTO REMUNERADO / KM ACOP REALIZADA)
    const custoImmu = totalKmAcop > 0 ? totalCustoRemunerado / totalKmAcop : 0;

    // Card 5: CUSTO EMPRESA (GASTO REAL / KM ACOP REALIZADA)
    const custoEmpresa = totalKmAcop > 0 ? totalGastoReal / totalKmAcop : 0;

    // Card 6: DIFERENÇA (CUSTO EMPRESA - CUSTO IMMU)
    const diferenca2 = custoEmpresa - custoImmu;
    // Variação percentual: se a diferença aumenta (fica mais positiva), é para mais (seta vermelha para cima)
    // Se diminui (fica mais negativa ou menos positiva), é para menos (seta verde para baixo)
    const variacao2Percentual = custoEmpresa > 0 ? Math.abs((diferenca2 / custoEmpresa) * 100) : 0;
    const variacao2Direcao = diferenca2 > 0 ? 'up' : 'down'; // up = vermelho, down = verde

    const resultado = {
      custoRemunerado,
      gastoReal,
      diferenca1,
      variacao1Percentual,
      variacao1Direcao,
      custoImmu,
      custoEmpresa,
      diferenca2,
      variacao2Percentual,
      variacao2Direcao,
    };

    console.log('✔️ Métricas de Compras calculadas:', resultado);
    return resultado;
  }, [comprasData]);

  // Calcula gastos reais por grupo de compras
  const comprasPorGrupo = useMemo(() => {
    if (!comprasGrupoData || comprasGrupoData.length === 0) {
      console.log('⚠️ Sem dados de grupos de compras');
      return {
        '001': 0,
        '002': 0,
        '003': 0,
        '004': 0,
        '005': 0,
      };
    }

    const grupos = {
      '001': 0, // COMBUSTÍVEL
      '002': 0, // LUBRIFICANTES
      '003': 0, // PNEUS
      '004': 0, // PEÇAS
      '005': 0, // ADMINISTRATIVO / PREDIAL
    };

    comprasGrupoData.forEach((row) => {
      // gr_analise vem no formato "001 - COMBUSTÍVEL", extrai apenas os 3 primeiros dígitos
      const grAnalise = String(row.gr_analise || '').substring(0, 3).trim();
      const gastoReal = Number(row.gasto_real || 0);

      if (grupos.hasOwnProperty(grAnalise)) {
        grupos[grAnalise] += gastoReal;
      }
    });

    console.log('✔️ Gastos reais por grupo calculados:', grupos);
    return grupos;
  }, [comprasGrupoData]);

  // Dados agregados para o gráfico comparativo Remunerado vs Realizado por grupo
  const dadosGraficoComparativoGrupos = useMemo(() => {
    if (!comprasGrupoData || comprasGrupoData.length === 0) {
      return [];
    }

    const gruposMap = {
      '001': { nome: 'COMBUSTÍVEL', remunerado: 0, realizado: 0 },
      '002': { nome: 'LUBRIFICANTES', remunerado: 0, realizado: 0 },
      '003': { nome: 'PNEUS', remunerado: 0, realizado: 0 },
      '004': { nome: 'PEÇAS', remunerado: 0, realizado: 0 },
      '005': { nome: 'ADMINISTRATIVO / PREDIAL', remunerado: 0, realizado: 0 },
    };

    comprasGrupoData.forEach((row) => {
      const grAnalise = String(row.gr_analise || '').substring(0, 3).trim();
      const remunerado = Number(row.remunerado || 0);
      const realizado = Number(row.gasto_real || 0);

      if (gruposMap.hasOwnProperty(grAnalise)) {
        gruposMap[grAnalise].remunerado += remunerado;
        gruposMap[grAnalise].realizado += realizado;
      }
    });

    const resultado = Object.keys(gruposMap)
      .map(key => ({ id: key, ...gruposMap[key] }))
      .sort((a, b) => parseInt(a.id) - parseInt(b.id));

    console.log('📊 Dados do Gráfico Comparativo Grupos:', resultado);
    return resultado;
  }, [comprasGrupoData]);

  // Dados agregados para o gráfico comparativo Programado vs Realizado por grupo
  const dadosGraficoProgramadoVsRealizado = useMemo(() => {
    if (!comprasGrupoData || comprasGrupoData.length === 0) {
      return [];
    }

    const gruposMap = {
      '001': { nome: 'COMBUSTÍVEL', programado: 0, realizado: 0 },
      '002': { nome: 'LUBRIFICANTES', programado: 0, realizado: 0 },
      '003': { nome: 'PNEUS', programado: 0, realizado: 0 },
      '004': { nome: 'PEÇAS', programado: 0, realizado: 0 },
      '005': { nome: 'ADMINISTRATIVO / PREDIAL', programado: 0, realizado: 0 },
    };

    comprasGrupoData.forEach((row) => {
      const grAnalise = String(row.gr_analise || '').substring(0, 3).trim();
      const programado = Number(row.programado || 0);
      const realizado = Number(row.gasto_real || 0);

      if (gruposMap.hasOwnProperty(grAnalise)) {
        gruposMap[grAnalise].programado += programado;
        gruposMap[grAnalise].realizado += realizado;
      }
    });

    const resultado = Object.keys(gruposMap)
      .map(key => ({ id: key, ...gruposMap[key] }))
      .sort((a, b) => parseInt(a.id) - parseInt(b.id));

    console.log('📊 Dados do Gráfico Programado vs Realizado:', resultado);
    return resultado;
  }, [comprasGrupoData]);



  // Função para criar gradiente baseado na eficiência (comentada para ajustes manuais)
  // AJUSTE MANUAL NECESSÁRIO: Defina corBase com a cor desejada (#8B9DC3)
  // e ajuste a interpolação de cores rgba conforme necessário
  // const criarGradiente = (eficiencia) => {
  //   const corBase = '#8B9DC3';
  //   const percentual = (eficiencia / 100) * 100;
  //   return `linear-gradient(135deg, ${corBase} 0%, rgba(139, 157, 195, ${0.7 + (percentual / 300)}) 100%)`;
  // };

  if (error && operacionalData.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // Helpers de formatação
  const fmt2 = (n) =>
    Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const fmtPercent = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')}%`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          minHeight: '100vh',
          height: '100vh',
          bgcolor: isDark ? '#1A202C' : '#F5F7FA',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* ===== HEADER ===== */}
        <Box
          sx={{
            background: isDark
              ? 'linear-gradient(135deg, #2D3748 0%, #1A202C 100%)'
              : 'linear-gradient(135deg, #00897B 0%, #0277BD 100%)',
            boxShadow: 'none',
            border: 'none',
            backdropFilter: 'blur(10px)',
            py: 1,
            px: { xs: 1, sm: 3 },
            position: 'sticky',
            top: 0,
            zIndex: 100,
            flexShrink: 0
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Botão Voltar */}
            <IconButton
              onClick={handleVoltarClick}
              sx={{
                color: 'white',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                }
              }}
              title="Voltar ao Menu"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
              </Box>
            </IconButton>

            {/* Título */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                <Lottie animationData={cartAnimation} loop autoplay style={{ width: '32px', height: '32px' }} />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '1.3rem', sm: '1.5rem' }
                  }}
                >
                  Indicadores Estratégicos
                </Typography>
              </Box>
            </Box>

            {/* Botão Dark Mode */}
            <IconButton
              onClick={() => setMode(isDark ? 'light' : 'dark')}
              sx={{
                color: 'white',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                }
              }}
              title={isDark ? 'Modo Claro' : 'Modo Escuro'}
            >
              {isDark ? (
                <LightModeRoundedIcon fontSize="large" />
              ) : (
                <DarkModeRoundedIcon fontSize="large" />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* ===== CONTEÚDO PRINCIPAL ===== */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'auto',
            px: { xs: 1, sm: 2, md: 3 },
            py: 2
          }}
        >
          {/* ===== FILTROS DE DATA ===== */}
          <Paper
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr', md: 'auto auto auto 1fr auto auto' },
              gap: { xs: 1, sm: 1.5, md: 2 },
              alignItems: 'center',
              p: 2,
              mb: 3,
              bgcolor: isDark ? '#2D3748' : '#FFFFFF',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterAltIcon sx={{ color: isDark ? '#667EEA' : '#764BA2', fontSize: '1.3rem' }} />
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>Filtro</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <DatePicker
                label="Início"
                value={startDate}
                onChange={(v) => setStartDate(v)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
              />
              <DatePicker
                label="Fim"
                value={endDate}
                onChange={(v) => setEndDate(v)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
              />
              <Button
                variant="contained"
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
                  textTransform: 'capitalize',
                  fontWeight: 600,
                  color: '#fff',
                  width: 110,
                  height: 40
                }}
                onClick={handleApplyFilter}
              >
                Filtrar
              </Button>
            </Box>

            <Button
              variant="contained"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
                textTransform: 'capitalize',
                fontWeight: 600,
                color: '#fff',
                width: 110,
                height: 40
              }}
              onClick={() => {
                setStartDate(currentDate.startOf('month'));
                setEndDate(currentDate.endOf('month'));
                setQueryStartDate(currentDate.startOf('month'));
                setQueryEndDate(currentDate.endOf('month'));
              }}
            >
              Limpar
            </Button>
          </Paper>

          {/* ===== SEÇÃO OPERACIONAL ===== */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', scrollBehavior: 'smooth' }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              📊 Indicadores Operacionais
            </Typography>

            {/* Loading ou erro */}
            {loadingDados && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, color: 'text.secondary' }}>Carregando dados...</Typography>
              </Box>
            )}

            {/* BACKGROUND ÚNICO - TODAS AS SEÇÕES DE CARDS */}
            <Box
              sx={{
                background: isDark ? 'rgba(45, 55, 72, 0.4)' : 'rgba(0, 128, 128, 0.04)',
                borderRadius: 2,
                p: { xs: 2, sm: 2.5, md: 3 },
                mb: 4,
              }}
            >
            {/* Cards de Eficiência - Layout responsivo, ordenados por eficiência */}
            {!loadingDados && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(7, 1fr)' },
                  gap: { xs: 0.75, sm: 1, md: 0.75 },
                  mb: 2,
                  autoFlow: 'dense'
                }}
              >
          {Object.entries(eficienciasPorEmpresa).length > 0 ? (
            Object.keys(eficienciasPorEmpresa)
              .sort((a, b) => eficienciasPorEmpresa[b].eficiencia - eficienciasPorEmpresa[a].eficiencia)
              .map((empresa, index) => {
              const dados = eficienciasPorEmpresa[empresa];
              if (!dados) return null;

              // const bgCard = criarGradiente(dados.eficiencia);
              const bgCard = '#B967FF';

              const tooltipContent = (
                <Box sx={{ fontSize: '0.85rem' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Km Programada: {fmt2(dados.kmProgramada)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                    Km Realizada: {fmt2(dados.kmRealizada)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    Km Perdida: {fmt2(dados.kmPerdida)}
                  </Typography>
                </Box>
              );

              return (
                <Tooltip 
                  key={empresa} 
                  title={tooltipContent} 
                  arrow
                  placement="top"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 10],
                          },
                        },
                      ],
                    },
                    tooltip: {
                      sx: {
                        backgroundColor: '#2a2a2a',
                        color: '#FFFFFF',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        fontSize: '0.8rem',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontFamily: '"Aptos Narrow", sans-serif',
                      },
                    },
                    arrow: {
                      sx: {
                        color: '#2a2a2a',
                      },
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        backgroundColor: bgCard,
                        p: 1,
                        borderRadius: '8px 8px 0 0',
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8
                      }}
                    >
                      <SpeedIcon sx={{ fontSize: '1.2rem', color: '#FFFFFF' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: '#FFFFFF',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.3,
                          fontFamily: '"Aptos Narrow", sans-serif',
                        }}
                      >
                        Eficiência de Quilometragem
                      </Typography>
                    </Box>
                    <Paper
                      elevation={3}
                      sx={{
                        background: isDark
                          ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                          : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        p: 1.2,
                        borderRadius: '0 0 8px 8px',
                        minHeight: '90px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: isDark
                            ? `0 12px 28px ${bgCard}40`
                            : `0 12px 28px ${bgCard}30`
                        }
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.4rem',
                          background: `linear-gradient(135deg, ${bgCard} 0%, ${bgCard}dd 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.3
                        }}
                      >
                        {fmtPercent(dados.eficiencia)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          fontSize: '0.7rem'
                        }}
                      >
                        {empresa}
                      </Typography>
                    </Paper>
                  </Box>
                </Tooltip>
              );
            })
          ) : (
            <Typography sx={{ color: 'text.secondary', py: 4, textAlign: 'center', fontSize: '1.1rem', gridColumn: '1 / -1' }}>
              Nenhum dado disponível para o período selecionado, selecione o mês anterior.
            </Typography>
          )}
          </Box>
        )}

            {/* Cards de Eficiência de Viagem - Layout responsivo, ordenados por eficiência */}
            {!loadingDados && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(7, 1fr)' },
                  gap: { xs: 0.75, sm: 1, md: 0.75 },
                  mb: 2,
                  autoFlow: 'dense'
                }}
              >
          {Object.entries(eficienciaViagensPorEmpresa).length > 0 ? (
            Object.keys(eficienciaViagensPorEmpresa)
              .sort((a, b) => eficienciaViagensPorEmpresa[b].eficiencia - eficienciaViagensPorEmpresa[a].eficiencia)
              .map((empresa, index) => {
              const dados = eficienciaViagensPorEmpresa[empresa];
              if (!dados) return null;

              // const bgCard = criarGradiente(dados.eficiencia);
              const bgCard = '#009688';

              const tooltipContent = (
                <Box sx={{ fontSize: '0.85rem' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                    V. Programadas: {fmt2(dados.viagensProgramadas)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                    V. Realizadas: {fmt2(dados.viagensRealizadas)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                    V. Perdidas: {fmt2(dados.viagensNaoRealizadas)}
                  </Typography>
                </Box>
              );

              return (
                <Tooltip 
                  key={empresa} 
                  title={tooltipContent} 
                  arrow
                  placement="top"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 10],
                          },
                        },
                      ],
                    },
                    tooltip: {
                      sx: {
                        backgroundColor: '#2a2a2a',
                        color: '#FFFFFF',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        fontSize: '0.8rem',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontFamily: '"Aptos Narrow", sans-serif',
                      },
                    },
                    arrow: {
                      sx: {
                        color: '#2a2a2a',
                      },
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        backgroundColor: bgCard,
                        p: 1,
                        borderRadius: '8px 8px 0 0',
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8
                      }}
                    >
                      <DirectionsCarIcon sx={{ fontSize: '1.2rem', color: '#FFFFFF' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: '#FFFFFF',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.3,
                          fontFamily: '"Aptos Narrow", sans-serif',
                        }}
                      >
                        Eficiência de Viagem
                      </Typography>
                    </Box>
                    <Paper
                      elevation={3}
                      sx={{
                        background: isDark
                          ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                          : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        p: 1.2,
                        borderRadius: '0 0 8px 8px',
                        minHeight: '90px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: isDark
                            ? `0 12px 28px ${bgCard}40`
                            : `0 12px 28px ${bgCard}30`
                        }
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.4rem',
                          background: `linear-gradient(135deg, ${bgCard} 0%, ${bgCard}dd 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.3
                        }}
                      >
                        {fmtPercent(dados.eficiencia)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          fontSize: '0.7rem'
                        }}
                      >
                        {empresa}
                      </Typography>
                    </Paper>
                  </Box>
                </Tooltip>
              );
            })
          ) : null}
        </Box>
      )}

            {/* CARDS - SÃO PEDRO (DETALHADO) */}
            {dadosSaoPedro && (
              <>
                {/* Cards de São Pedro - Métricas Principais */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(7, 1fr)' },
                    gap: { xs: 0.75, sm: 1, md: 0.75 },
                    mb: 2,
                  }}
                >
              {/* Card 0: Frota Operacional (NOVO) */}
            {frotaSaoPedroRecente && (
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#189AD3',
                    p: 1.2,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    🚌 Frota Operacional
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(24, 154, 211, 0.2)'
                        : '0 12px 28px rgba(24, 154, 211, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #189AD3 0%, #1565C0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    {Math.floor(frotaSaoPedroRecente.frota)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    São Pedro
                  </Typography>
                </Paper>
              </Box>
            )}

              {/* Card 1: Km Programada */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#189AD3',
                  p: 1.2,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}

              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  🎯 KM Programada
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(24, 154, 211, 0.2)'
                      : '0 12px 28px rgba(24, 154, 211, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #189AD3 0%, #1565C0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmt2(dadosSaoPedro.kmProgramada)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  São Pedro
                </Typography>
              </Paper>
            </Box>

            {/* Card 2: Km Realizada */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#189AD3',
                  p: 1.2,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  ✅ Km Realizada
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(24, 154, 211, 0.2)'
                      : '0 12px 28px rgba(24, 154, 211, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #189AD3 0%, #1565C0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmt2(dadosSaoPedro.kmRealizada)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  São Pedro
                </Typography>
              </Paper>
            </Box>

            {/* Card 3: Km Perdida com Percentual */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#189AD3',
                  p: 1.2,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  ⚠️ Km Perdida
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(24, 154, 211, 0.2)'
                      : '0 12px 28px rgba(24, 154, 211, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #189AD3 0%, #1565C0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmt2(dadosSaoPedro.kmPerdida)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  {fmtPercent(dadosSaoPedro.percentualKmPerdida)}
                </Typography>
              </Paper>
            </Box>

            {/* Card 4: V. Programadas */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#189AD3',
                  p: 1,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  🚗 Viagem Programada
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(24, 154, 211, 0.2)'
                      : '0 12px 28px rgba(24, 154, 211, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #189AD3 0%, #1565C0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmt2(dadosSaoPedro.viagensProgramadas)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  São Pedro
                </Typography>
              </Paper>
            </Box>

            {/* Card 5: V. Realizadas */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#189AD3',
                  p: 1,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  ✅ Viagem Realizada
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(24, 154, 211, 0.2)'
                      : '0 12px 28px rgba(24, 154, 211, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #189AD3 0%, #1565C0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmt2(dadosSaoPedro.viagensRealizadas)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  São Pedro
                </Typography>
              </Paper>
            </Box>

            {/* Card 6: V. Perdidas com Percentual */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#189AD3',
                  p: 1,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  ⚠️ Viagem Perdida
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(24, 154, 211, 0.2)'
                      : '0 12px 28px rgba(24, 154, 211, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #189AD3 0%, #1565C0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmt2(dadosSaoPedro.viagensNaoRealizadas)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  {fmtPercent(dadosSaoPedro.percentualViagensPerdidas)}
                </Typography>
              </Paper>
            </Box>
            </Box>

            {/* Metas Históricas - São Pedro */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(7, 1fr)' },
                gap: { xs: 0.75, sm: 1, md: 0.75 },
              }}
            >
              {/* Card 7: Meta Histórica KM */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#00B159',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    🎯 Meta Ef. de Quilometragem
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(0, 177, 89, 0.2)'
                        : '0 12px 28px rgba(0, 177, 89, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #00B159 0%, #009245 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    {fmtPercent(metaKmQuiletragem)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Meta
                  </Typography>
                </Paper>
              </Box>

            {/* Card 8: Meta Histórica Viagem */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#00B159',
                  p: 1,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  🎯 Meta Ef. de Viagem
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(0, 177, 89, 0.2)'
                      : '0 12px 28px rgba(0, 177, 89, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #00B159 0%, #009245 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmtPercent(metaViagemSP)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  Meta
                </Typography>
              </Paper>
            </Box>

            {/* Card 9: Maior Ef. Quilometragem */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#00B159',
                  p: 1,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  ⭐ Maior Ef. de Quilometragem
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(0, 177, 89, 0.2)'
                      : '0 12px 28px rgba(0, 177, 89, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #00B159 0%, #009245 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmtPercent(maiorEfKm.eficiencia)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  {maiorEfKm.mesFormatado || 'Melhor'}
                </Typography>
              </Paper>
            </Box>

            {/* Card 10: Maior Ef. Viagem */}
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  backgroundColor: '#00B159',
                  p: 1,
                  borderRadius: '8px 8px 0 0',
                  mb: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#FFFFFF',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.3,
                  }}
                >
                  ⭐ Maior Ef. de Viagem
                </Typography>
              </Box>
              <Paper
                elevation={3}
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                    : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                  p: 1.2,
                  borderRadius: '0 0 8px 8px',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark
                      ? '0 12px 28px rgba(0, 177, 89, 0.2)'
                      : '0 12px 28px rgba(0, 177, 89, 0.3)'
                  }
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.3rem',
                    background: 'linear-gradient(135deg, #00B159 0%, #009245 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 0.3
                  }}
                >
                  {fmtPercent(maiorEfViagem.eficiencia)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  {maiorEfViagem.mesFormatado || 'Melhor'}
                </Typography>
              </Paper>
            </Box>
            </Box>
            </>
            )}
        </Box>

        {/* ===== SEÇÃO COMPRAS ===== */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            mt: 3,
            color: 'text.primary',
            fontSize: { xs: '1rem', sm: '1.2rem' },
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <ShoppingCartIcon sx={{ fontSize: '1.5rem', color: '#64B5F6' }} /> Indicadores de Compras
        </Typography>

        <Box
          sx={{
            background: isDark ? 'rgba(45, 55, 72, 0.4)' : 'rgba(0, 128, 128, 0.04)',
            borderRadius: 2,
            p: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {/* FILTROS DE DATA - COMPRAS */}
          <Paper
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr', md: 'auto auto auto 1fr auto auto' },
              gap: { xs: 1, sm: 1.5, md: 2 },
              alignItems: 'center',
              p: 2,
              mb: 3,
              bgcolor: isDark ? '#2D3748' : '#FFFFFF',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterAltIcon sx={{ color: isDark ? '#667EEA' : '#764BA2', fontSize: '1.3rem' }} />
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>Compras</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <DatePicker
                label="Início"
                value={startDateCompras}
                onChange={(v) => setStartDateCompras(v)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
              />
              <DatePicker
                label="Fim"
                value={endDateCompras}
                onChange={(v) => setEndDateCompras(v)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
              />
              <Button
                variant="contained"
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
                  textTransform: 'capitalize',
                  fontWeight: 600,
                  color: '#fff',
                  width: 110,
                  height: 40
                }}
                onClick={handleApplyFilterCompras}
              >
                Filtrar
              </Button>
            </Box>

            <Button
              variant="contained"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
                textTransform: 'capitalize',
                fontWeight: 600,
                color: '#fff',
                width: 110,
                height: 40
              }}
              onClick={() => {
                setStartDateCompras(currentDate.startOf('month'));
                setEndDateCompras(currentDate.endOf('month'));
                setQueryStartDateCompras(currentDate.startOf('month'));
                setQueryEndDateCompras(currentDate.endOf('month'));
              }}
            >
              Limpar
            </Button>
          </Paper>
          {loadingCompras && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                Carregando dados de compras...
              </Typography>
            </Box>
          )}

          {!loadingCompras && comprasData.length === 0 && (
            <Typography sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
              Nenhum dado de compras disponível para o período selecionado
            </Typography>
          )}

          {!loadingCompras && comprasData.length > 0 && (
            <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
                gap: { xs: 0.75, sm: 1, md: 0.75 },
              }}
            >
              {/* Card 1: Custo Remunerado */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#3F79FC',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    💳 Custo Rem.
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(63, 121, 252, 0.2)'
                        : '0 12px 28px rgba(63, 121, 252, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #3F79FC 0%, #1E40AF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(metricsCompras.custoRemunerado)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Remunerado
                  </Typography>
                </Paper>
              </Box>

              {/* Card 2: Realizado (Gasto Real) */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#3F79FC',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    💰 Realizado
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(63, 121, 252, 0.2)'
                        : '0 12px 28px rgba(63, 121, 252, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #3F79FC 0%, #1E40AF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(metricsCompras.gastoReal)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Gasto Real
                  </Typography>
                </Paper>
              </Box>

              {/* Card 3: Diferença */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#3F79FC',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    📊 Diferença
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(63, 121, 252, 0.2)'
                        : '0 12px 28px rgba(63, 121, 252, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #3F79FC 0%, #1E40AF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(metricsCompras.diferenca1)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: metricsCompras.variacao1Direcao === 'up' ? '#FF6B6B' : '#51CF66',
                      fontSize: '0.7rem'
                    }}
                  >
                    {metricsCompras.variacao1Percentual.toFixed(2).replace('.', ',')}%
                  </Typography>
                </Paper>
              </Box>

              {/* Card 4: Custo Immu */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#3F79FC',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    💰 Custo Immu
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(63, 121, 252, 0.2)'
                        : '0 12px 28px rgba(63, 121, 252, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #3F79FC 0%, #1E40AF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    {fmt2(metricsCompras.custoImmu)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    km
                  </Typography>
                </Paper>
              </Box>

              {/* Card 5: Custo Empresa */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#3F79FC',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    🏢 Custo Emp.
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(63, 121, 252, 0.2)'
                        : '0 12px 28px rgba(63, 121, 252, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #3F79FC 0%, #1E40AF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    {fmt2(metricsCompras.custoEmpresa)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    km
                  </Typography>
                </Paper>
              </Box>

              {/* Card 6: Diferença (Custo Empresa - Custo Immu) */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#3F79FC',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    📊 Dif.
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(63, 121, 252, 0.2)'
                        : '0 12px 28px rgba(63, 121, 252, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #3F79FC 0%, #1E40AF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    {fmt2(metricsCompras.diferenca2)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: metricsCompras.variacao2Direcao === 'up' ? '#FF6B6B' : '#51CF66',
                      fontSize: '0.7rem'
                    }}
                  >
                    {metricsCompras.variacao2Percentual.toFixed(2).replace('.', ',')}%
                  </Typography>
                </Paper>
              </Box>
            </Box>

            {/* GRID DOS GRUPOS DE COMPRAS - Abaixo dos 6 primeiros cards */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                gap: { xs: 0.75, sm: 1, md: 0.75 },
                mt: 2,
              }}
            >
              {/* Card Grupo 1: COMBUSTÍVEL */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#FFBF24',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    ⛽ Combustível
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(255, 191, 36, 0.2)'
                        : '0 12px 28px rgba(255, 191, 36, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #FFBF24 0%, #FFA000 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(comprasPorGrupo['001'])}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Realizado
                  </Typography>
                </Paper>
              </Box>

              {/* Card Grupo 2: LUBRIFICANTES */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#00FF66',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    💧 Lubrificantes
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(0, 255, 102, 0.2)'
                        : '0 12px 28px rgba(0, 255, 102, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #00FF66 0%, #00BB2E 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(comprasPorGrupo['002'])}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Realizado
                  </Typography>
                </Paper>
              </Box>

              {/* Card Grupo 3: PNEUS */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#FFFF00',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#000000',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    🛞 Pneus
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(255, 255, 0, 0.2)'
                        : '0 12px 28px rgba(255, 255, 0, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #FFFF00 0%, #FFD700 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(comprasPorGrupo['003'])}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Realizado
                  </Typography>
                </Paper>
              </Box>

              {/* Card Grupo 4: PEÇAS */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#FF5722',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    🔧 Peças
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(255, 87, 34, 0.2)'
                        : '0 12px 28px rgba(255, 87, 34, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #FF5722 0%, #D84315 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(comprasPorGrupo['004'])}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Realizado
                  </Typography>
                </Paper>
              </Box>

              {/* Card Grupo 5: ADMINISTRATIVO / PREDIAL */}
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    backgroundColor: '#FF00FF',
                    p: 1,
                    borderRadius: '8px 8px 0 0',
                    mb: 0.5
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                    }}
                  >
                    🏛️ Admin
                  </Typography>
                </Box>
                <Paper
                  elevation={3}
                  sx={{
                    background: isDark
                      ? 'linear-gradient(135deg, #2D3748 0%, #374151 100%)'
                      : 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                    p: 1.2,
                    borderRadius: '0 0 8px 8px',
                    minHeight: '90px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDark
                        ? '0 12px 28px rgba(255, 0, 255, 0.2)'
                        : '0 12px 28px rgba(255, 0, 255, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.3rem',
                      background: 'linear-gradient(135deg, #FF00FF 0%, #DD00FF 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.3
                    }}
                  >
                    R$ {fmt2(comprasPorGrupo['005'])}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.7rem'
                    }}
                  >
                    Realizado
                  </Typography>
                </Paper>
              </Box>
            </Box>

            {/* GRÁFICOS DE COMPRAS */}
            <Box sx={{ mt: 4, mb: 3 }}>
              {/* Gráfico: COMPARATIVO REMUNERADO vs REALIZADO - MÊS ATUAL */}
              <Box
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #1F2937 0%, #111827 100%)'
                    : 'linear-gradient(135deg, #00887C 0%, #08ACCC 100%)',
                  borderRadius: 2,
                  p: { xs: 2, sm: 3, md: 4 },
                  boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  mb: 4
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#ffffff',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 3,
                    textAlign: 'center',
                  }}
                >
                  📊 Remunerado vs Realizado
                </Typography>

                {dadosGraficoComparativoGrupos.length === 0 ? (
                  <Typography sx={{ color: 'text.secondary', py: 8, textAlign: 'center' }}>
                    Sem dados disponíveis para o gráfico
                  </Typography>
                ) : (
                  <Box sx={{ width: '100%', height: 410, mb: 3 }}>
                    <Chart
                      type="bar"
                      series={[
                        {
                          name: 'Remunerado',
                          data: dadosGraficoComparativoGrupos?.map(item => item?.remunerado || 0) || [],
                        },
                        {
                          name: 'Realizado',
                          data: dadosGraficoComparativoGrupos?.map(item => item?.realizado || 0) || [],
                        }
                      ]}
                      options={{
                        chart: {
                          type: 'bar',
                          toolbar: { show: true },
                          background: 'transparent',
                        },
                        colors: ['#BE29EC', '#51CF66'],
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '70%',
                            borderRadius: 6,
                            spacing: 12,
                            dataLabels: {
                              position: 'top',
                              enabled: true,
                              offsetY: -25,
                              style: {
                                fontSize: '14px',
                                fontWeight: 600,
                                colors: ['#FFFFFF'],
                              },
                              formatter: function(val) {
                                // Formata número com separador de milhar: 3175214 => 3.175.214
                                const num = Math.round(Number(val));
                                return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                              }
                            },
                          },
                        },
                        xaxis: {
                          categories: dadosGraficoComparativoGrupos.map(item => item.nome),
                          labels: {
                            style: {
                              colors: '#FFFFFF',
                              fontSize: '14px',
                            },
                            trim: false,
                            hideOverlappingLabels: false,
                            rotate: -45,
                            offsetY: 8,
                          },
                          tickPlacement: 'on',
                        },
                        yaxis: {
                          labels: {
                            style: {
                              colors: '#FFFFFF',
                              fontSize: '13px',
                            },
                            formatter: function(val) {
                              return `R$ ${(val / 1000).toFixed(1)}k`;
                            },
                          },
                        },
                        grid: {
                          borderColor: 'rgba(255, 255, 255, 0.08)',
                          strokeDashArray: 4,
                          padding: {
                            top: 20,
                            right: 120,
                            bottom: 50,
                            left: 80,
                          },
                        },
                        legend: {
                          labels: {
                            colors: '#FFFFFF',
                            fontSize: '14px',
                            useSeriesColors: false,
                          },
                          position: 'top',
                          horizontalAlign: 'left',
                          offsetY: -10,
                        },
                        tooltip: {
                          enabled: true,
                          theme: 'dark',
                          shared: true,
                          intersect: false,
                          style: {
                            fontSize: '12px',
                          },
                          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                            const categoria = w.globals.categoryLabels && w.globals.categoryLabels[dataPointIndex] 
                              ? w.globals.categoryLabels[dataPointIndex]
                              : (w.config.xaxis.categories && w.config.xaxis.categories[dataPointIndex])
                              ? w.config.xaxis.categories[dataPointIndex]
                              : `Categoria ${dataPointIndex + 1}`;
                            const remunerado = series[0][dataPointIndex];
                            const realizado = series[1][dataPointIndex];
                            const diferenca = realizado - remunerado;
                            const percentual = remunerado > 0 ? ((diferenca / remunerado) * 100) : 0;
                            const isAlto = realizado > remunerado;
                            
                            return `
                              <div style="padding: 12px; background: rgba(0,0,0,0.7); border-radius: 4px; border-left: 3px solid ${isAlto ? '#FF6B6B' : '#51CF66'};">
                                <div style="font-weight: bold; color: #FFFFFF; margin-bottom: 8px; font-size: 13px;">${categoria}</div>
                                <div style="color: #E618A4; margin-bottom: 4px; font-size: 12px;">● Remunerado: R$ ${remunerado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                <div style="color: ${isAlto ? '#FB2E01' : '#51CF66'}; margin-bottom: 6px; font-size: 12px;">● Realizado: R$ ${realizado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                <div style="color: ${isAlto ? '#FB2E01' : '#51CF66'}; font-size: 12px; font-weight: bold;">
                                  ${isAlto ? '▲' : '▼'} ${Math.abs(percentual).toFixed(2).replace('.', ',')}%
                                </div>
                              </div>
                            `;
                          },
                        },
                      }}
                      height={450}
                    />
                  </Box>
                )}
              </Box>

              {/* Gráfico: COMPARATIVO PROGRAMADO vs REALIZADO - MÊS ATUAL */}
              <Box
                sx={{
                  background: isDark
                    ? 'linear-gradient(135deg, #1F2937 0%, #111827 100%)'
                    : 'linear-gradient(135deg, #00887C 0%, #08ACCC 100%)',
                  borderRadius: 2,
                  p: { xs: 2, sm: 3, md: 4 },
                  boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#ffffff',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 3,
                    textAlign: 'center',
                  }}
                >
                  📅 Programado vs Realizado
                </Typography>

                {dadosGraficoProgramadoVsRealizado.length === 0 ? (
                  <Typography sx={{ color: 'text.secondary', py: 8, textAlign: 'center' }}>
                    Sem dados disponíveis para o gráfico
                  </Typography>
                ) : (
                  <Box sx={{ width: '100%', height: 410, mb: 3 }}>
                    <Chart
                      type="bar"
                      series={[
                        {
                          name: 'Programado',
                          data: dadosGraficoProgramadoVsRealizado?.map(item => item?.programado || 0) || [],
                        },
                        {
                          name: 'Realizado',
                          data: dadosGraficoProgramadoVsRealizado?.map(item => item?.realizado || 0) || [],
                        }
                      ]}
                      options={{
                        chart: {
                          type: 'bar',
                          toolbar: { 
                            show: true,
                            tools: {
                              download: true,
                              selection: true,
                              zoom: true,
                              zoomin: true,
                              zoomout: true,
                              pan: true,
                              reset: true
                            }
                          },
                          background: 'transparent',
                        },
                        colors: ['#FFBF00', '#51CF66'],
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '70%',
                            borderRadius: 6,
                            spacing: 12,
                            dataLabels: {
                              position: 'top',
                              enabled: true,
                              offsetY: -25,
                              style: {
                                fontSize: '14px',
                                fontWeight: 600,
                                colors: ['#FFFFFF'],
                              },
                              formatter: function(val) {
                                const num = Math.round(Number(val));
                                return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                              }
                            },
                          },
                        },
                        xaxis: {
                          categories: dadosGraficoProgramadoVsRealizado?.map(item => item?.nome) || [],
                          labels: {
                            style: {
                              colors: '#FFFFFF',
                              fontSize: '14px',
                            },
                            trim: false,
                            hideOverlappingLabels: false,
                            rotate: -45,
                            offsetY: 8,
                          },
                          tickPlacement: 'on',
                        },
                        yaxis: {
                          labels: {
                            style: {
                              colors: '#FFFFFF',
                              fontSize: '13px',
                            },
                            formatter: function(val) {
                              return `R$ ${(val / 1000).toFixed(1)}k`;
                            },
                          },
                        },
                        grid: {
                          borderColor: 'rgba(255, 255, 255, 0.08)',
                          strokeDashArray: 4,
                          padding: {
                            top: 20,
                            right: 120,
                            bottom: 50,
                            left: 80,
                          },
                        },
                        legend: {
                          labels: {
                            colors: '#FFFFFF',
                            fontSize: '14px',
                            useSeriesColors: false,
                          },
                          position: 'top',
                          horizontalAlign: 'left',
                          offsetY: -10,
                        },
                        tooltip: {
                          enabled: true,
                          theme: 'dark',
                          shared: true,
                          intersect: false,
                          style: {
                            fontSize: '12px',
                          },
                          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                            const categoria = w.globals.categoryLabels && w.globals.categoryLabels[dataPointIndex] 
                              ? w.globals.categoryLabels[dataPointIndex]
                              : (w.config.xaxis.categories && w.config.xaxis.categories[dataPointIndex])
                              ? w.config.xaxis.categories[dataPointIndex]
                              : `Categoria ${dataPointIndex + 1}`;
                            const programado = series[0][dataPointIndex];
                            const realizado = series[1][dataPointIndex];
                            const diferenca = realizado - programado;
                            const percentual = programado > 0 ? ((diferenca / programado) * 100) : 0;
                            const isAlto = realizado > programado;
                            
                            return `
                              <div style="padding: 12px; background: rgba(0,0,0,0.7); border-radius: 4px; border-left: 3px solid ${isAlto ? '#FF6B6B' : '#51CF66'};">
                                <div style="font-weight: bold; color: #FFFFFF; margin-bottom: 8px; font-size: 13px;">${categoria}</div>
                                <div style="color: #01A9F4; margin-bottom: 4px; font-size: 12px;">● Programado: R$ ${programado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                <div style="color: ${isAlto ? '#FF6B6B' : '#51CF66'}; margin-bottom: 6px; font-size: 12px;">● Realizado: R$ ${realizado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                <div style="color: ${isAlto ? '#FF6B6B' : '#51CF66'}; font-size: 12px; font-weight: bold;">
                                  ${isAlto ? '▲' : '▼'} ${Math.abs(percentual).toFixed(2).replace('.', ',')}%
                                </div>
                              </div>
                            `;
                          },
                        },
                      }}
                      height={450}
                    />
                  </Box>
                )}
              </Box>
            </Box>
            </>
            )}
        </Box>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}